import { useState, useEffect, useRef, useMemo } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import { useZones } from '../../hooks/useZones';
import DepartureTopStrip from '../../components/ui/DepartureTopStrip';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const BTN_BASE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  fontWeight: 700,
  border: 'none',
  padding: '5px 12px',
  cursor: 'pointer',
  lineHeight: 1.4,
  transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease, opacity 120ms ease',
};

const BTN_AMBER = {
  ...BTN_BASE,
  background: 'var(--c-brand)',
  color: '#fff',
};

const BTN_DANGER = {
  ...BTN_BASE,
  background: 'transparent',
  border: '1px solid var(--c-danger)',
  color: 'var(--c-danger)',
};

const BTN_GHOST = {
  ...BTN_BASE,
  background: 'transparent',
  color: 'var(--c-ink-2)',
  border: '1px solid var(--c-line-soft)',
};

const TH_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--c-ink-3)',
  padding: '10px 14px',
  textAlign: 'left',
  borderBottom: '1px solid var(--c-line-hard)',
  background: 'var(--c-bg-2)',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '.06em',
  padding: '10px 14px',
  borderBottom: '1px solid var(--c-line-soft)',
  color: 'var(--c-ink)',
  verticalAlign: 'middle',
};

const TD_NAME_STYLE = {
  ...TD_STYLE,
  fontFamily: "'Geist', sans-serif",
  fontSize: 13,
  letterSpacing: 'normal',
  fontWeight: 500,
  color: 'var(--c-ink)',
};

const SECTION_LABEL = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--c-ink-3)',
  marginBottom: 'var(--sp-3)',
};

// ---------------------------------------------------------------------------
// Loading bar
// ---------------------------------------------------------------------------

function LoadingBar() {
  return (
    <>
      <style>{`
        @keyframes sma-bar { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { .sma-loading-bar { display: none; } }
      `}</style>
      <div style={{ position: 'relative', height: 2, background: 'var(--c-line-soft)', overflow: 'hidden', marginBottom: 'var(--sp-6)' }}>
        <div
          className="sma-loading-bar"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--c-brand)',
            animation: 'sma-bar 900ms ease-in-out infinite',
          }}
        />
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '.14em',
        color: 'var(--c-ink-3)',
        textTransform: 'uppercase',
        padding: '0 0 var(--sp-6)',
      }}>
        LOADING…
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ primary, secondary }) {
  return (
    <div style={{
      padding: 'var(--sp-12) var(--pad-h)',
      textAlign: 'center',
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      letterSpacing: '.14em',
      color: 'var(--c-ink-3)',
      textTransform: 'uppercase',
    }}>
      <div style={{ marginBottom: 6 }}>{primary}</div>
      {secondary && (
        <div style={{ fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>{secondary}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hoverable row helper
// ---------------------------------------------------------------------------

function HoverRow({ children, style, ...rest }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      {...rest}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'var(--c-bg-2)' : 'transparent',
        transition: 'background 120ms ease',
        ...style,
      }}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

function AssignModal({ supervisor, onConfirm, onCancel }) {
  const { zones } = useZones();
  const AREAS = useMemo(() => zones.map((z) => z.name), [zones]);
  const [selectedArea, setSelectedArea] = useState('');
  const selectRef = useRef(null);
  const modalRef = useRef(null);

  // Default to the first zone once it loads (zones is async).
  useEffect(() => {
    if (!selectedArea && AREAS.length > 0) setSelectedArea(AREAS[0]);
  }, [AREAS, selectedArea]);

  // Focus trap: focus first element on open, Tab/Shift+Tab cycles within modal, Escape closes
  useEffect(() => {
    selectRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--c-bg)',
          border: '1px solid var(--c-line-hard)',
          width: 380,
          maxWidth: '92vw',
          padding: 24,
        }}
      >
        {/* Title */}
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'var(--c-ink-3)',
            marginBottom: 4,
          }}
        >
          ASSIGN SITE MANAGER
        </div>
        <div
          style={{
            fontFamily: "'Geist', sans-serif",
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--c-ink)',
            marginBottom: 'var(--sp-8)',
          }}
        >
          {supervisor.display_name || supervisor.ixora_user_id || supervisor.id}
        </div>

        {/* Area select */}
        <label
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'var(--c-ink-3)',
            display: 'block',
            marginBottom: 6,
          }}
        >
          ALLOTTED AREA
        </label>
        <select
          ref={selectRef}
          value={selectedArea}
          onChange={(e) => setSelectedArea(e.target.value)}
          style={{
            width: '100%',
            padding: '7px 10px',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            border: '1px solid var(--c-line-hard)',
            borderRadius: 0,
            background: 'var(--c-bg-2)',
            color: 'var(--c-ink)',
            marginBottom: 'var(--sp-8)',
            minHeight: 'unset',
          }}
        >
          {AREAS.map((a) => (
            <option key={a} value={a}>
              {a.toUpperCase()}
            </option>
          ))}
        </select>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            style={{ ...BTN_AMBER, flex: 1, padding: '9px 12px' }}
            onClick={() => onConfirm(selectedArea)}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-brand-2, #C2410C)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--c-brand)'; }}
          >
            CONFIRM
          </button>
          <button
            style={{ ...BTN_GHOST, padding: '9px 18px' }}
            onClick={onCancel}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--c-ink-3)'; e.currentTarget.style.color = 'var(--c-ink)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-line-soft)'; e.currentTarget.style.color = 'var(--c-ink-2)'; }}
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SiteManagerAdmin = () => {
  const { zones } = useZones();
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // docId → true while writing
  const [modal, setModal] = useState(null); // { supervisor } | null

  // -------------------------------------------------------------------------
  // Real-time subscription
  // -------------------------------------------------------------------------

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'supervisors'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const aId = a.ixora_user_id || a.id;
          const bId = b.ixora_user_id || b.id;
          return aId.localeCompare(bId, undefined, { numeric: true });
        });
        setSupervisors(list);
        setLoading(false);
      },
      (err) => {
        console.error('SiteManagerAdmin: snapshot error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const assign = async (supervisor, allotment) => {
    setPending((p) => ({ ...p, [supervisor.id]: true }));
    setModal(null);
    // Look up the zone so we can write the multi-site array shape too. Old
    // `ixora_allotment` string is preserved for back-compat during the read
    // migration; cleanup PR will drop it once every consumer reads the array.
    const zone = zones.find((z) => z.name === allotment);
    try {
      await updateDoc(doc(db, 'supervisors', supervisor.id), {
        userRole: 'siteManager',
        ixora_allotment: allotment,
        ...(zone
          ? {
              allotted_site_ids: [zone.id],
              allotted_areas: zone.region ? [zone.region] : [],
            }
          : {}),
      });
    } catch (err) {
      console.error('SiteManagerAdmin: assign failed', err);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[supervisor.id]; return n; });
    }
  };

  const revoke = async (supervisor) => {
    setPending((p) => ({ ...p, [supervisor.id]: true }));
    try {
      await updateDoc(doc(db, 'supervisors', supervisor.id), {
        userRole: deleteField(),
        ixora_allotment: deleteField(),
        allotted_site_ids: deleteField(),
        allotted_areas: deleteField(),
      });
    } catch (err) {
      console.error('SiteManagerAdmin: revoke failed', err);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[supervisor.id]; return n; });
    }
  };

  const handleMakeSiteMgr = (supervisor) => {
    if (supervisor.ixora_allotment) {
      assign(supervisor, supervisor.ixora_allotment);
    } else {
      setModal({ supervisor });
    }
  };

  // -------------------------------------------------------------------------
  // Derived lists
  // -------------------------------------------------------------------------

  const siteManagers = supervisors.filter((s) => s.userRole === 'siteManager');
  const others = supervisors.filter((s) => s.userRole !== 'siteManager');

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="db-page">
      <DepartureTopStrip />

      {/* Ribbon */}
      <div className="db-ribbon">
        <div className="db-ribbon-title">
          SITE MANAGER ASSIGNMENTS
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '.12em',
              fontWeight: 700,
              color: 'var(--c-brand)',
              marginLeft: 14,
            }}
          >
            {siteManagers.length} ACTIVE
          </span>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)' }}>
        {loading ? (
          <LoadingBar />
        ) : (
          <>
            {/* ---- Current site managers ---- */}
            <div style={{
              marginBottom: 'var(--sp-12)',
              borderLeft: '3px solid var(--c-brand)',
              paddingLeft: 'var(--sp-4)',
            }}>
              <div style={SECTION_LABEL}>ACTIVE SITE MANAGERS</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={TH_STYLE}>NAME</th>
                      <th style={{ ...TH_STYLE, textAlign: 'right' }}>ID</th>
                      <th style={TH_STYLE}>ALLOTMENT</th>
                      <th style={{ ...TH_STYLE, textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteManagers.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ padding: 0, borderBottom: '1px solid var(--c-line-soft)' }}>
                          <EmptyState
                            primary="NO SITE MANAGERS YET"
                            secondary="Promote a supervisor below to get started"
                          />
                        </td>
                      </tr>
                    ) : (
                      siteManagers.map((s) => (
                        <HoverRow key={s.id}>
                          <td style={TD_NAME_STYLE}>{s.display_name || '—'}</td>
                          <td style={{ ...TD_STYLE, textAlign: 'right', fontVariantNumeric: 'tabular-nums slashed-zero' }}>
                            {s.ixora_user_id || s.id}
                          </td>
                          <td style={TD_STYLE}>
                            {s.ixora_allotment
                              ? s.ixora_allotment.toUpperCase()
                              : <span style={{ color: 'var(--c-ink-3)' }}>—</span>}
                          </td>
                          <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                            <button
                              style={pending[s.id]
                                ? { ...BTN_DANGER, opacity: 0.6, cursor: 'not-allowed' }
                                : BTN_DANGER}
                              disabled={!!pending[s.id]}
                              onClick={() => revoke(s)}
                              onMouseEnter={(e) => { if (!pending[s.id]) e.currentTarget.style.background = 'rgba(220,38,38,0.05)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              {pending[s.id] ? '…' : 'REVOKE'}
                            </button>
                          </td>
                        </HoverRow>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ---- All other supervisors ---- */}
            <div style={{
              borderLeft: '3px solid var(--c-line-hard)',
              paddingLeft: 'var(--sp-4)',
            }}>
              <div style={SECTION_LABEL}>ALL SUPERVISORS</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={TH_STYLE}>NAME</th>
                      <th style={{ ...TH_STYLE, textAlign: 'right' }}>ID</th>
                      <th style={TH_STYLE}>CURRENT ALLOTMENT</th>
                      <th style={{ ...TH_STYLE, textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {others.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ padding: 0, borderBottom: '1px solid var(--c-line-soft)' }}>
                          <EmptyState primary="ALL SUPERVISORS ARE SITE MANAGERS" />
                        </td>
                      </tr>
                    ) : (
                      others.map((s) => (
                        <HoverRow key={s.id}>
                          <td style={TD_NAME_STYLE}>{s.display_name || '—'}</td>
                          <td style={{ ...TD_STYLE, textAlign: 'right', fontVariantNumeric: 'tabular-nums slashed-zero' }}>
                            {s.ixora_user_id || s.id}
                          </td>
                          <td style={TD_STYLE}>
                            {s.ixora_allotment
                              ? s.ixora_allotment.toUpperCase()
                              : <span style={{ color: 'var(--c-ink-3)' }}>—</span>}
                          </td>
                          <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                            <button
                              style={pending[s.id]
                                ? { ...BTN_AMBER, opacity: 0.6, cursor: 'not-allowed' }
                                : BTN_AMBER}
                              disabled={!!pending[s.id]}
                              onClick={() => handleMakeSiteMgr(s)}
                              onMouseEnter={(e) => { if (!pending[s.id]) e.currentTarget.style.background = 'var(--c-brand-2, #C2410C)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--c-brand)'; }}
                            >
                              {pending[s.id] ? '…' : 'MAKE SITE MGR'}
                            </button>
                          </td>
                        </HoverRow>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Modal */}
      {modal && (
        <AssignModal
          supervisor={modal.supervisor}
          onConfirm={(area) => assign(modal.supervisor, area)}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default SiteManagerAdmin;
